import { call, put, takeLatest, select } from 'redux-saga/effects';
import AlgoliaClient from 'utils/algoliaService';
import _forEach from 'lodash/forEach';
import _isEmpty from 'lodash/isEmpty';
import Config from 'utils/getEnvConfig';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { getAlgoliaTopicsQuery } from 'containers/Main/utils';
import {
  processing,
  nextPageProcessing,
  getBlogsResult,
  getBlogsTopicFacetsResult,
} from './actions';
import { GET_BLOGS, GET_BLOGS_TOPIC_FACETS } from './constants';

const {
  ALGOLIA: { ARTICLES_INDEX },
} = Config;

function* getBlogsSaga({ payload }) {
  const {
    topicFilters,
    blogTypesFilters,
    queryFilter,
    hitsPerPage = 12,
    page = 0,
  } = payload;
  try {
    if (page === 0) yield put(processing(true));
    else yield put(nextPageProcessing(true));

    const clientDetails = yield select(makeSelectClientDetails());

    const AlgoliaIndex = AlgoliaClient.initIndex(ARTICLES_INDEX);

    let filterQuery = 'blog:true';
    if (!clientDetails) {
      filterQuery += ' AND hasClient:false';
    } else {
      filterQuery += ` AND (hasClient:false OR client.shortName:'${clientDetails.shortName}')`;
    }
    if (!_isEmpty(topicFilters)) {
      filterQuery = ' AND ';
      _forEach(topicFilters, (value, index) => {
        filterQuery += `topics:'${value}'`;
        if (index !== topicFilters.length - 1) {
          filterQuery += ' OR ';
        }
      });
    }
    if (!_isEmpty(blogTypesFilters) && blogTypesFilters.length === 1) {
      if (blogTypesFilters[0] === 'Company Blog') {
        filterQuery += ' AND official:true';
      } else if (blogTypesFilters[0] === 'Ambassador Blog') {
        filterQuery += ' AND official:false';
      }
    }

    const algoliaTopicsQuery = yield call(getAlgoliaTopicsQuery, 'topics');

    const response = yield call(() =>
      AlgoliaIndex.search(queryFilter, {
        page,
        hitsPerPage,
        getRankingInfo: false,
        filters: `${filterQuery}${algoliaTopicsQuery}`,
      }),
    );
    const { hits, nbHits, nbPages, processingTimeMS } = response;
    const finalHits = hits.map(({ organization, ...el }) => ({
      ...el,
      organization: organization
        ? organization.map(org => ({
            fields: org,
          }))
        : [],
    }));
    const data = {
      hits: finalHits,
      nbHits,
      nbPages,
      processingTimeMS,
    };

    const topicsFacets = yield call(() =>
      AlgoliaIndex.searchForFacetValues('topics', '', {
        maxFacetHits: 50,
        filters: `${filterQuery}${algoliaTopicsQuery}`,
      }),
    );
    const filters = topicsFacets.facetHits.map(el => ({
      value: el.value,
    }));
    yield put(getBlogsResult({ page, data, filters }));
  } catch (error) {
    yield put(getBlogsResult({ error: true }));
  } finally {
    if (page === 0) yield put(processing(false));
    else yield put(nextPageProcessing(false));
  }
}

function* getBlogsTopicFacetsSaga({ payload }) {
  const { query } = payload;

  try {
    const clientDetails = yield select(makeSelectClientDetails());

    const AlgoliaIndex = AlgoliaClient.initIndex(ARTICLES_INDEX);

    let filterQuery = 'blog:true';
    if (!clientDetails) {
      filterQuery += ' AND hasClient:false';
    } else {
      filterQuery += ` AND (hasClient:false OR client.shortName:'${clientDetails.shortName}')`;
    }

    const algoliaTopicsQuery = yield call(getAlgoliaTopicsQuery, 'topics');

    const filterOptions = yield call(() =>
      AlgoliaIndex.searchForFacetValues('topics', query, {
        maxFacetHits: 50,
        filters: `${filterQuery}${algoliaTopicsQuery}`,
      }),
    );
    const filters = filterOptions.facetHits.map(el => ({
      value: el.value,
    }));
    yield put(getBlogsTopicFacetsResult(filters));
  } catch (error) {
    yield put(getBlogsTopicFacetsResult([]));
  }
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(GET_BLOGS, getBlogsSaga);
  yield takeLatest(GET_BLOGS_TOPIC_FACETS, getBlogsTopicFacetsSaga);
}
